<template>
  <div class="portefeuille-section portefeuille-module portefeuille-vehicles-module">
    <v-container class="section-content">
      <button class="back-button" @click="goBack">
        <span class="fa fa-arrow-left"></span> {{ $t('common.back') }}
      </button>

      <div class="page-header">
        <div class="header-content">
          <h1 class="page-title">{{ $t('portefeuille.vehicles') }}</h1>
          <p class="page-subtitle">{{ $t('portefeuille.vehicles_subtitle') }}</p>
        </div>
      </div>

      <div class="toolbar-section">
        <div class="search-box">
          <span class="fa fa-search search-icon"></span>
          <input
            v-model="searchQuery"
            type="text"
            :placeholder="$t('portefeuille.search_brand')"
            class="search-input"
          />
        </div>
        <button class="add-button-new" @click="openAddDialog">
          <span class="fa fa-plus"></span> {{ $t('portefeuille.add_vehicle') }}
        </button>
      </div>

      <div v-if="loading" class="empty-state">
        <span class="fa fa-spinner fa-spin empty-icon"></span>
        <p class="empty-text">{{ $t('common.loading') }}</p>
      </div>

      <div v-else-if="filteredVehicles.length === 0" class="empty-state">
        <span class="fa fa-car empty-icon"></span>
        <p class="empty-text">{{ $t('portefeuille.no_vehicles') }}</p>
      </div>

      <div v-else class="table-wrapper">
        <table class="vehicles-table">
          <thead>
            <tr>
              <th>{{ $t('portefeuille.vehicle') }}</th>
              <th>{{ $t('portefeuille.license_plate') }}</th>
              <th>{{ $t('portefeuille.power') }}</th>
              <th>{{ $t('common.action') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="vehicle in filteredVehicles" :key="vehicle._id" class="table-row">
              <td class="vehicle-cell">
                <span class="vehicle-icon fa fa-car"></span>
                <div class="vehicle-info">
                  <div class="vehicle-name">{{ vehicle.marque }} {{ vehicle.model }}</div>
                  <div class="vehicle-year">{{ vehicle.dateCircule }} · {{ getCategoryLabel(vehicle.categorie) }}</div>
                </div>
              </td>
              <td>{{ vehicle.immatriculation }}</td>
              <td>{{ vehicle.force }}</td>
              <td class="action-cell">
                <div class="action-buttons">
                  <button class="action-btn" @click="editVehicle(vehicle)" title="Modifier">
                    <span class="fa fa-edit"></span>
                  </button>
                  <button class="action-btn" @click="requestDeleteVehicle(vehicle._id)" title="Supprimer">
                    <span class="fa fa-trash"></span>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-container>

    <v-dialog v-model="itemDialog" max-width="700" persistent>
      <v-card elevation="24" class="border-radius portefeuille-modal-accent-orange">
        <v-card-text style="background: #fafafa">
          <div class="wizard-tab-content">
            <div role="tabpanel" class="wizard-tab-container">
              <div class="fields">
                <div class="field-title font-weight-bold">
                  {{ editingItem ? $t('portefeuille.edit_vehicle') : $t('portefeuille.add_vehicle') }}
                </div>

                <!-- ÉTAPE 1: CATÉGORISATION DU VÉHICULE -->
                <div class="row mt-3">
                  <div class="col-sm-12">
                    <label class="field-label font-weight-bold">Catégorisation du véhicule <span style="color: #e74c3c;">*</span></label>
                    <div class="category-buttons">
                      <div class="category-btn">
                        <input type="radio" v-model="formData.categorie" value="tourisme" id="cat-tourisme">
                        <label for="cat-tourisme"><span class="fa fa-car"></span> Véhicule particulier</label>
                      </div>
                      <div class="category-btn">
                        <input type="radio" v-model="formData.categorie" value="2_roues" id="cat-2roues">
                        <label for="cat-2roues"><span class="fa fa-motorcycle"></span> Véhicule 2 roues</label>
                      </div>
                      <div class="category-btn">
                        <input type="radio" v-model="formData.categorie" value="utilitaire" id="cat-utilitaire">
                        <label for="cat-utilitaire"><span class="fa fa-truck"></span> Véhicule utilitaire</label>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Conditions: Si UTILITAIRE -->
                <div v-show="formData.categorie === 'utilitaire'" class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Option utilitaire</label>
                      <v-autocomplete
                        v-model="formData.optionUtilitaire"
                        :items="['Pour Compte', 'Pour Tiers']"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                </div>

                <!-- Conditions: Si UTILITAIRE - Tonnage -->
                <div v-show="formData.categorie === 'utilitaire'" class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Tonnage / Carrosserie</label>
                      <v-autocomplete
                        v-model="formData.tonnage"
                        :items="getTonnageOptions"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 2: MARQUE & MODÈLE (TOUS) -->
                <div class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">{{ $t('portefeuille.vehicle_brand') }} <span style="color: #e74c3c;">*</span></label>
                      <v-autocomplete
                        v-model="formData.marque"
                        :items="getMarques"
                        @change="updateModels"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">{{ $t('portefeuille.vehicle_model') }} <span style="color: #e74c3c;">*</span></label>
                      <v-autocomplete
                        v-model="formData.model"
                        :items="filteredModels"
                        :disabled="!formData.marque"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 3: IMMATRICULATION & CHASSIS (TOUS) -->
                <div class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">{{ $t('portefeuille.vehicle_license') }} <span style="color: #e74c3c;">*</span></label>
                      <input v-model="formData.immatriculation" @input="validateImmatriculation" type="text" class="field-input" placeholder="XX-XXX-XX ou XX-XXXX-XX" maxlength="10" />
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Numéro de châssis (VIN)</label>
                      <input v-model="formData.numeroChassis" type="text" class="field-input" maxlength="17" />
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 4: DATE & ÂGE (TOUS) -->
                <div class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Date de mise en circulation</label>
                      <input v-model="formData.dateCircule" type="date" class="field-input" @change="calculateAge" />
                    </div>
                  </div>
                  <div v-if="formData.dateCircule" class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Âge du véhicule</label>
                      <input v-model="calculatedAge" type="text" class="field-input" disabled />
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 5: PUISSANCE & PLACES (CONDITIONS) -->
                <div v-show="formData.categorie !== '2_roues'" class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">{{ $t('portefeuille.vehicle_power') }}</label>
                      <v-autocomplete
                        v-model="formData.force"
                        :items="powerOptions"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Nombre de places</label>
                      <v-autocomplete
                        v-model="formData.nbplace"
                        :items="seatsOptions"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 6: CARBURANT (TOUS) -->
                <div class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Carburant</label>
                      <v-autocomplete
                        v-model="formData.carburation"
                        :items="['Essence', 'Diésel', 'Hybride', 'Électrique']"
                        dense
                        outlined
                        clearable
                        placeholder="Sélectionner..."
                        class="field-autocomplete"
                      ></v-autocomplete>
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 7: CYLINDRÉE (SI 2-ROUES) -->
                <div v-show="formData.categorie === '2_roues'" class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Cylindrée (cm³)</label>
                      <input v-model="formData.cylindre" type="number" class="field-input" placeholder="125" />
                    </div>
                  </div>
                </div>

                <!-- ÉTAPE 8: VALEURS (SI PAS 2-ROUES) -->
                <div v-show="formData.categorie !== '2_roues'" class="row mt-3">
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Valeur neuve (FCFA)</label>
                      <input v-model="formData.valeur" type="number" class="field-input" />
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="field-group field-default">
                      <label class="field-label">Valeur actuelle (FCFA)</label>
                      <input v-model="formData.valeur_venale" type="number" class="field-input" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="wizard-card-footer clearfix">
              <div class="wizard-footer-left">
                <button type="button" class="wizard-btn" @click="itemDialog = false">{{ $t('common.cancel') }}</button>
              </div>
              <div class="wizard-footer-right">
                <button type="button" class="wizard-btn" @click="saveVehicle">{{ $t('common.save') }}</button>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460" persistent>
      <v-card elevation="24" class="border-radius portefeuille-modal-accent-orange">
        <v-card-text style="background: #fafafa">
          <div class="wizard-tab-content">
            <div role="tabpanel" class="wizard-tab-container">
              <div class="fields text-center">
                <div class="field-title font-weight-bold">{{ $t('common.delete') }}</div>
                <div class="field-sub-title">{{ $t('common.confirm_delete') }}</div>
              </div>
            </div>
            <div class="wizard-card-footer clearfix">
              <div class="wizard-footer-left">
                <button type="button" class="wizard-btn" @click="cancelDeleteVehicle">{{ $t('common.cancel') }}</button>
              </div>
              <div class="wizard-footer-right">
                <button type="button" class="wizard-btn" @click="confirmDeleteVehicle">{{ $t('common.delete') }}</button>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'PortefeuilleVehicles',
  data() {
    return {
      searchQuery: '',
      itemDialog: false,
      deleteDialog: false,
      pendingDeleteId: null,
      editingItem: null,
      calculatedAge: 0,
      formData: {
        categorie: '',
        marque: '',
        model: '',
        immatriculation: '',
        numeroChassis: '',
        dateCircule: '',
        carburation: '',
        force: '',
        nbplace: '',
        optionUtilitaire: '',
        tonnage: '',
        cylindre: '',
        valeur: '',
        valeur_venale: ''
      },
      marquesList: {
        tourisme: ['Peugeot', 'Renault', 'Citroën', 'Toyota', 'Hyundai', 'Kia', 'Dacia', 'Nissan', 'Ford', 'Volkswagen', 'Fiat', 'Mercedes-Benz', 'BMW', 'Audi', 'Autre'],
        '2_roues': ['Yamaha', 'Honda', 'Kawasaki', 'Suzuki', 'KTM', 'Harley-Davidson', 'Royal Enfield', 'Autre'],
        utilitaire: ['Peugeot', 'Renault', 'Citroën', 'Ford', 'Iveco', 'Mitsubishi', 'Toyota', 'Nissan', 'Autre']
      },
      modelsList: {
        'Peugeot|tourisme': ['308', '3008', '5008', '208', '2008', '108', '407', '508'],
        'Renault|tourisme': ['Clio', 'Megane', 'Scenic', 'Espace', 'Dacia Sandero', 'Twingo'],
        'Citroën|tourisme': ['C3', 'C4', 'C5', 'C1', 'Berlingo'],
        'Toyota|tourisme': ['Corolla', 'Yaris', 'Camry', 'Avensis', 'Prius'],
        'Peugeot|utilitaire': ['Boxer', 'Partner', 'Bipper'],
        'Renault|utilitaire': ['Master', 'Trafic', 'Kangoo'],
        'Citroën|utilitaire': ['Berlingo', 'Jumper', 'Jumpy'],
        'Yamaha|2_roues': ['MT-07', 'MT-09', 'YZF-R3', 'YZF-R6', 'FZ-09'],
        'Honda|2_roues': ['CB500', 'CB300', 'CB125', 'CRF250', 'PCX'],
        'Kawasaki|2_roues': ['Ninja 400', 'Ninja 650', 'Z400', 'Z900'],
        'default': []
      }
    }
  },
  computed: {
    ...mapState('vehicles', ['loading']),
    ...mapGetters('vehicles', ['listeVehicles']),
    filteredVehicles() {
      if (!this.searchQuery) return this.listeVehicles
      return this.listeVehicles.filter(vehicle =>
        vehicle.marque.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },
    getMarques() {
      return this.marquesList[this.formData.categorie] || []
    },
    filteredModels() {
      const key = `${this.formData.marque}|${this.formData.categorie}`
      return this.modelsList[key] || this.modelsList.default
    },
    getTonnageOptions() {
      const options = [
        'Autre carosserie jusqu\'à 3,5 tonnes',
        'Autre carosserie plus de 3,5 tonnes'
      ]
      if (this.formData.optionUtilitaire === 'Pour Compte') {
        options.unshift('carosserie tourisme')
      }
      return options
    },
    powerOptions() {
      const options = []
      for (let i = 1; i <= 24; i++) {
        options.push(i.toString())
      }
      options.push('24+')
      return options
    },
    seatsOptions() {
      const options = []
      for (let i = 2; i <= 20; i++) {
        options.push(i)
      }
      return options
    }
  },
  methods: {
    ...mapActions('vehicles', ['fetchVehicles', 'addVehicle', 'updateVehicle', 'deleteVehicle']),

    formatDateForInput(date) {
      if (!date) return ''
      // Convertir la date ISO ou string en format YYYY-MM-DD pour l'input type="date"
      const d = new Date(date)
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const year = d.getFullYear()
      return `${year}-${month}-${day}`
    },

    showError(message) {
      // Fermer tous les toasts existants avant d'afficher un nouveau
      this.$toasted.clear()
      this.$toasted.show(message, {
        theme: 'toasted-primary',
        position: 'top-center',
        type: 'error',
        duration: 3000,
        singleton: true
      })
    },

    async loadVehicles() {
      try {
        await this.fetchVehicles()
      } catch (error) {
        this.$toasted.show(this.$t('common.error_loading'), {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 3000
        })
      }
    },

    openAddDialog() {
      this.editingItem = null
      this.formData = {
        categorie: '',
        marque: '',
        model: '',
        immatriculation: '',
        numeroChassis: '',
        dateCircule: '',
        carburation: '',
        force: '',
        nbplace: '',
        optionUtilitaire: '',
        tonnage: '',
        cylindre: '',
        valeur: '',
        valeur_venale: ''
      }
      this.calculatedAge = 0
      this.itemDialog = true
    },

    editVehicle(vehicle) {
      this.editingItem = vehicle
      this.formData = {
        categorie: vehicle.categorie || '',
        marque: vehicle.marque || '',
        model: vehicle.model || '',
        immatriculation: vehicle.immatriculation || '',
        numeroChassis: vehicle.numeroChassis || '',
        dateCircule: this.formatDateForInput(vehicle.dateCircule),
        carburation: vehicle.carburation || '',
        force: vehicle.force ? String(vehicle.force) : '',
        nbplace: vehicle.nbplace ? Number(vehicle.nbplace) : '',
        optionUtilitaire: vehicle.optionUtilitaire || '',
        tonnage: vehicle.tonnage || '',
        cylindre: vehicle.cylindre || '',
        valeur: vehicle.valeur || '',
        valeur_venale: vehicle.valeur_venale || ''
      }
      if (vehicle.dateCircule) {
        this.calculateAge()
      }
      this.itemDialog = true
    },

    updateModels() {
      // Réinitialiser le modèle quand on change la marque
      this.formData.model = ''
    },

    calculateAge() {
      if (!this.formData.dateCircule) {
        this.calculatedAge = 0
        return
      }
      const today = new Date()
      const birthDate = new Date(this.formData.dateCircule)
      this.calculatedAge = today.getFullYear() - birthDate.getFullYear()
    },

    validateImmatriculation() {
      // Formats acceptés: XX-XXXX-XX ou XX-XXX-XX (2 caractères, tiret, 3 ou 4 caractères, tiret, 2 caractères)
      const regex = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{3,4}-[A-Za-z0-9]{2}$/
      if (!regex.test(this.formData.immatriculation)) {
        // Supprimer les caractères non autorisés (tout sauf alphanumériques et tirets)
        this.formData.immatriculation = this.formData.immatriculation.replace(/[^A-Za-z0-9-]/g, '')
      }
    },

    requestDeleteVehicle(id) {
      this.pendingDeleteId = id
      this.deleteDialog = true
    },

    cancelDeleteVehicle() {
      this.pendingDeleteId = null
      this.deleteDialog = false
    },

    async confirmDeleteVehicle() {
      if (!this.pendingDeleteId) return

      try {
        await this.deleteVehicle(this.pendingDeleteId)
        this.$toasted.show(this.$t('common.deleted'), {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 3000
        })
      } catch (error) {
        this.$toasted.show(this.$t('common.error_delete'), {
          theme: 'toasted-primary',
          position: 'top-center',
          duration: 3000
        })
      } finally {
        this.cancelDeleteVehicle()
      }
    },

    async saveVehicle() {
      // Validation des champs obligatoires
      if (!this.formData.categorie) {
        this.showError(this.$t('portefeuille.vehicle_category_required'))
        return
      }
      if (!this.formData.marque) {
        this.showError(this.$t('portefeuille.vehicle_brand_required'))
        return
      }
      if (!this.formData.model) {
        this.showError(this.$t('portefeuille.vehicle_model_required'))
        return
      }
      if (!this.formData.immatriculation) {
        this.showError(this.$t('portefeuille.vehicle_license_required'))
        return
      }

      // Validation du format d'immatriculation (XX-XXXX-XX ou XX-XXX-XX)
      const immatRegex = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{3,4}-[A-Za-z0-9]{2}$/
      if (!immatRegex.test(this.formData.immatriculation)) {
        this.showError('Format d\'immatriculation non respecté (XX-XXXX-XX ou XX-XXX-XX)')
        return
      }

      try {
        if (this.editingItem) {
          await this.updateVehicle({ id: this.editingItem._id, vehicleData: this.formData })
          this.$toasted.clear()
          this.$toasted.show(this.$t('common.updated'), {
            theme: 'toasted-primary',
            position: 'top-center',
            type: 'success',
            duration: 3000,
            singleton: true
          })
        } else {
          await this.addVehicle(this.formData)
          this.$toasted.clear()
          this.$toasted.show(this.$t('common.saved'), {
            theme: 'toasted-primary',
            position: 'top-center',
            type: 'success',
            duration: 3000,
            singleton: true
          })
        }
        this.itemDialog = false
      } catch (error) {
        this.showError(this.$t('common.error_saving'))
      }
    },

    getCategoryLabel(category) {
      const labels = {
        tourisme: 'Véhicule particulier',
        '2_roues': 'Véhicule 2 roues',
        utilitaire: 'Véhicule utilitaire'
      }
      return labels[category] || category
    },

    goBack() {
      this.$router.push({ name: 'portefeuille' })
    }
  },
  mounted() {
    this.loadVehicles()
  }
}
</script>

<style scoped>
.portefeuille-section {
  padding: 2rem;
  padding-top: 100px;
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: 'Lato', sans-serif;
}

.portefeuille-modal-accent-orange {
  --custom-color-primary: #ffa409;
}

.page-header {
  margin-bottom: 1.5rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.875rem;
  color: #333;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  margin-bottom: 1rem;
}

.back-button:hover {
  color: #ffa409;
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 3.2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.page-subtitle {
  font-size: 1.4rem;
  color: #666;
  margin: 0;
}

.section-content {
  /* Vuetify v-container padding par defaut pour alignement */
}

.toolbar-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 1rem;
}

.search-box {
  flex: 1;
  position: relative;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 0.8rem 1rem;
  display: flex;
  align-items: center;
}

.search-icon {
  color: #999;
  margin-right: 0.8rem;
  font-size: 0.875rem;
}

.search-input {
  border: none;
  outline: none;
  flex: 1;
  font-size: 1rem;
  color: #333;
  background: transparent;
  font-family: 'Lato', sans-serif;
}

.search-input::placeholder {
  color: #999;
}

.add-button-new {
  background: #ffa409;
  border: none;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
}

.add-button-new:hover {
  background: #ff9500;
  box-shadow: 0 4px 12px rgba(255, 164, 9, 0.3);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background: white;
  border-radius: 8px;
}

.empty-icon {
  font-size: 4rem;
  color: #ddd;
  margin-bottom: 1rem;
}

.empty-text {
  font-size: 1.1rem;
  color: #999;
  margin: 0;
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.vehicles-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1.15rem;
}

.vehicles-table thead {
  background-color: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
}

.vehicles-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  font-size: 1rem;
  letter-spacing: 0.5px;
}

.vehicles-table th:last-child {
  text-align: right;
}

.table-row {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background-color: #fafafa;
}

.table-row td {
  padding: 1rem;
  color: #333;
}

.vehicle-cell {
  display: flex;
  align-items: center;
  gap: 0.8rem;
}

.vehicle-icon {
  font-size: 1.5rem;
  color: #ffa409;
}

.vehicle-info {
  display: flex;
  flex-direction: column;
}

.vehicle-name {
  font-weight: 600;
  color: #1a1a1a;
}

.vehicle-year {
  font-size: 0.85rem;
  color: #999;
}

.action-cell {
  text-align: right;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-btn {
  background: transparent;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #666;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: #f0f0f0;
  color: #ffa409;
}

.input-field {
  margin-bottom: 1rem;
}

.field-autocomplete {
  margin-top: 0.5rem;
}

.field-autocomplete >>> .v-input__control {
  min-height: 45px;
}

.field-autocomplete >>> .v-text-field__details {
  display: none;
}

.category-buttons {
  display: flex;
  gap: 1rem;
  margin: 1rem 0;
  flex-wrap: wrap;
}

.category-btn {
  flex: 1;
  min-width: 150px;
  position: relative;
}

.category-btn input {
  display: none;
}

.category-btn label {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem;
  border: 2px solid #ddd;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  background-color: white;
  font-weight: 500;
  font-size: 0.875rem;
  gap: 0.5rem;
}

.category-btn label span {
  font-size: 1.2rem;
}

.category-btn input:checked + label {
  border-color: #ffa409;
  background-color: #fff7e8;
  color: #333;
}

.category-btn label:hover {
  border-color: #ffa409;
  box-shadow: 0 2px 8px rgba(255, 164, 9, 0.2);
}

@media only screen and (max-width: 768px) {
  .portefeuille-section {
    padding: 1rem;
    padding-top: 80px;
  }

  .page-header {
    flex-direction: column;
  }

  .page-title {
    font-size: 1.75rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }

  .back-button {
    font-size: 1rem;
  }

  .toolbar-section {
    flex-direction: column;
  }

  .search-input {
    font-size: 1rem;
  }

  .add-button-new {
    font-size: 1rem;
  }

  .vehicles-table {
    font-size: 0.95rem;
  }

  .vehicles-table th {
    font-size: 0.85rem;
  }

  .vehicles-table th,
  .table-row td {
    padding: 0.8rem;
  }

  .vehicle-name {
    font-size: 0.95rem;
  }

  .vehicle-year {
    font-size: 0.85rem;
  }

  .vehicle-cell {
    flex-direction: column;
    align-items: flex-start;
  }

  .empty-text {
    font-size: 1rem;
  }

  .category-btn label {
    font-size: 0.95rem;
  }

  .category-buttons {
    flex-direction: column;
  }

  .category-btn {
    min-width: 100%;
  }
}
</style>
